import { postCall, getCall, putCall, deleteCall} from "./api";
import { getActiveLegalUnit, invalidateOrgEmployees, invalidateSelfEmployee } from "./globalData";

/**
 * Every write below drops the cached directory.
 *
 * This lives in the service rather than at the call sites because there are
 * roughly forty of those, and asking each one to remember is why only two
 * places in the whole app invalidated anything. A screen that changes a name,
 * a designation or an active flag should not also have to know that six other
 * screens are reading a cached copy of it.
 *
 * Only on success: a rejected write leaves the server unchanged, so throwing
 * the cache away would cost a refetch to arrive at the same data.
 */
const invalidatingDirectory = (res) => {
  if (res?.success !== false) invalidateOrgEmployees();
  return res;
};

// A write to your own record changes both the directory and "me".
const invalidatingSelf = (res) => {
  if (res?.success !== false) invalidateSelfEmployee();
  return invalidatingDirectory(res);
};

export const createEmployee = async (input = {}) => {
  return invalidatingDirectory(await postCall({ path: `/employee/onboarding`, bodyParams: input, }));
};

// ---- Lifecycle + career timeline (Performance module) ----
export const getPositionHistory = async (employeeId) =>
  await getCall({ path: `/employee/${employeeId}/position-history` });

export const confirmEmployee = async (employeeId, input = {}) =>
  invalidatingDirectory(await postCall({ path: `/employee/${employeeId}/confirm`, bodyParams: input }));

export const setLifecycleStatus = async (employeeId, input = {}) =>
  invalidatingDirectory(await postCall({ path: `/employee/${employeeId}/lifecycle-status`, bodyParams: input }));

/**
 * Recording a resignation is not accepting it.
 *
 * This writes the request and changes no status — the person keeps working
 * while somebody decides. Accepting it is setLifecycleStatus above with
 * "notice_period", which reads the proposed date off the request.
 */
export const requestExit = async (employeeId, input = {}) =>
  invalidatingDirectory(await postCall({ path: `/employee/${employeeId}/exit-request`, bodyParams: input }));

export const cancelExitRequest = async (employeeId) =>
  invalidatingDirectory(await deleteCall({ path: `/employee/${employeeId}/exit-request` }));

// What is still outstanding before somebody can be marked as left. Read-only,
// so a screen can show it before anybody presses anything.
export const getExitReadiness = async (employeeId) =>
  await getCall({ path: `/employee/${employeeId}/exit-readiness` });

// promotion/transfer as one action (optionally applies a salary revision)
export const promoteEmployee = async (employeeId, input = {}) =>
  invalidatingDirectory(await postCall({ path: `/employee/${employeeId}/promote`, bodyParams: input }));

// The caller's own record, in full. Distinct from the directory, which is a
// colleague's view of a person and carries ten fields — not enough to know
// whether you have finished onboarding, let alone to prefill the form.
export const getSelfEmployee = async () => {
  return await getCall({ path: `/employee/self` });
};

export const getAllEmployee = async () => {
  return await getCall({ path: `/employee`, });
};

export const fetchEmployeeDetailsById = async (employeeId) => {
  return await getCall({ path: `/employee/${employeeId}`, });
};

export const updateEmployeeDetailsById = async (employeeId, input = {}) => {
  return invalidatingDirectory(await putCall({ path: `/employee/${employeeId}/onboarding`, bodyParams: input, }));
};

// employee self-service profile update (the signed-in employee updates their own
// record); used by the onboarding profile wizard. Submitting hasBeenOnboarded:true
// marks their profile complete for this org.
export const selfOnboardingUpdate = async (input = {}) => {
  return invalidatingSelf(await putCall({ path: `/employee/self-onboarding`, bodyParams: input, }));
};

export const fetchEmployeesByIdArray = async (employeeIds) => {
  return await getCall({ path: `/employee/id?employee=${employeeIds}`, });
};

export const sendEmployeeOnboardingEmail = async (employeeId) => {
  return invalidatingDirectory(await postCall({ path: `/employee/${employeeId}/send-email`, }));
};

// Activate / deactivate (offboard) an employee — PUT /employee/:id accepts isActive.
export const setEmployeeActive = async (employeeId, isActive) => {
  return invalidatingDirectory(await putCall({ path: `/employee/${employeeId}`, bodyParams: { isActive }, }));
};

// Offboard: deactivate + persist exit details ({ exitReason, lastWorkingDay, exitNotes }).
export const offboardEmployee = async (employeeId, details = {}) => {
  return invalidatingDirectory(await putCall({ path: `/employee/${employeeId}`, bodyParams: { isActive: false, offboardedAt: new Date().toISOString(), ...details }, }));
};

// Entity-level offboard: remove an employee from a specific legal entity.
export const removeEmployeeFromLegalUnit = async (legalUnitId, employeeId) => {
  return invalidatingDirectory(await deleteCall({ path: `/legal-unit-employee/${legalUnitId}/employee/${employeeId}`, }));
};

export const addEmployeeToLegalUnit = async (employeeId) => {
  const legalUnit = getActiveLegalUnit();
  const legalUnitId = legalUnit?._id;
  return invalidatingDirectory(await postCall({ path: `/legal-unit-employee/${legalUnitId}/employee`, bodyParams: { employeeId, }, }));
};

export const getEmployeesByDepartment = async (id) => {
  return await getCall({ path: `/employee/by-department/${id}`, });
};

